import { DeleteDialog, GlobalMenu } from '@components';
import { AssetContext } from '@pages/Asset/Asset.query';
import { useContext } from 'react';
import { useState } from '@hookstate/core';
import { NavigationContext } from '@layout/navigation/navigation';
import { deleteContact } from '@services/queries/contact';
import { changeContact } from '@services/queries/contact/changeContact/changeContact';
import { getPermissionData } from '@utils';
import { useQueryClient, useMutation } from 'react-query';
import { useParams } from 'react-router-dom';
import { IContact } from '@models/Contact.model';
import { getProductValue } from '@utils/getProductValue';
import { Container } from './ContactsItemMenu.styles';

interface IContactsItemMenu {
  contact: IContact;
}

const ContactsItemMenu = ({ contact }: IContactsItemMenu) => {
  const { selectedContactId, openEditContact, equipmentQuery } =
    useContext(AssetContext);
  const { role } = useContext(NavigationContext);

  const params = useParams<{ equipmentId: string }>();
  const openDeleteDialog = useState(false);

  const queryClient = useQueryClient();

  const mutation = useMutation(deleteContact, {
    onSuccess: () => {
      selectedContactId.set(null);
      queryClient.invalidateQueries([
        'equipments',
        params.equipmentId,
        'contacts',
      ]);
    },
  });

  const changeMutation = useMutation(changeContact, {
    onSuccess: () => {
      queryClient.invalidateQueries([
        'equipments',
        params.equipmentId,
        'contacts',
      ]);
    },
  });

  const editPermission = getPermissionData({
    owner: Boolean(contact.ownerStudioId),
    action: 'edit',
    role,
    type: 'Contact',
  });

  const favoritePermission = getPermissionData({
    owner: Boolean(contact.ownerStudioId),
    action: 'favorite',
    role,
    type: 'Contact',
  });

  const deletePermission = getPermissionData({
    owner: Boolean(contact.ownerStudioId),
    action: 'delete',
    role,
    type: 'Contact',
  });

  const { manufacturerId, productId } = getProductValue(equipmentQuery?.data);

  const hasProduct = manufacturerId && productId;
  const isEnabled = editPermission.allowed && hasProduct;

  return (
    <Container>
      <GlobalMenu
        align="end"
        title="Options"
        itemList={[
          {
            label: 'Edit',
            iconName: 'edit',
            disabled: !isEnabled,
            disableToolTip: editPermission.allowed,
            toolTipText: editPermission.message,
            toolTipId: 'editContactTooltip',
            onClick: () => {
              if (isEnabled) {
                openEditContact.set(true);
              }
            },
          },
          {
            label: contact.favorite
              ? 'Remove from Favorites'
              : 'Add to Favorites',
            iconName: contact.favorite ? 'star' : 'star-empty',
            disabled: !favoritePermission.allowed,
            disableToolTip: favoritePermission.allowed,
            toolTipText: favoritePermission.message,
            toolTipId: 'favoriteContactTooltip',
            onClick: () => {
              if (favoritePermission.allowed) {
                changeMutation.mutate({
                  data: {
                    contactId: contact.id,
                    favorite: !contact.favorite,
                  },
                });
              }
            },
          },
          {
            label: 'Delete',
            iconName: 'trash',
            disabled: !deletePermission.allowed,
            disableToolTip: deletePermission.allowed,
            toolTipText: deletePermission.message,
            toolTipId: 'deleteContactTooltip',
            onClick: () => {
              if (deletePermission.allowed) {
                openDeleteDialog.set(true);
              }
            },
          },
        ]}
      />

      <DeleteDialog
        open={openDeleteDialog}
        onDelete={() => {
          mutation.mutate(contact.id);
        }}
      />
    </Container>
  );
};

export { ContactsItemMenu };
